import { getJwtUserId, getTokenCookie, setTokenCookie } from '@root/utils/cookies/cookies';
import { SentryBreadcrumb } from 'utils/SentryHelper';

import { getRuntimeEnv } from '@/_utils/environment';

import { SCRIBE_API_URL } from './apiUrl';
import { isCurrentUrlWhiteListed } from './urls';

export const signOut = async (navigateToSignInPage = true) => {
  try {
    const userSignedOut = localStorage.getItem('userSignedOut');
    const { store } = await import('../store.js');
    const { signOut: signOutAction } = await import('../slices/authSlice');

    if (navigateToSignInPage) {
      const { frontendUrl } = getRuntimeEnv();

      if (userSignedOut === '401' || userSignedOut === '403') {
        const messageSuffix = userSignedOut === '401' ? 'Please sign in again.' : 'Your IP address is not allowed.';

        global.window.location.replace(
          `${frontendUrl}/signin?err=${encodeURIComponent(`For security reasons, you have been signed out. ${messageSuffix}`)}`
        );
      } else if (process.env.NEXT_PUBLIC_VERCEL_URL) {
        global.window.location.replace(`/signin`);
      } else {
        global.window.location.replace(`${frontendUrl}/signin`);
      }
    }
    store.dispatch(signOutAction());
  } catch (e) {
    console.error('Failed to sign out', e);
  }
};

const refreshPromises = new Map();

const fetchNewAccessToken = async ({ refreshToken }: { refreshToken?: string }) => {
  const userId = getJwtUserId(refreshToken);

  if (userId === null) throw new Error('Refresh token is invalid.');
  const currentPromise = refreshPromises.get(userId);

  if (currentPromise) {
    return currentPromise;
  }

  const promise = fetch(`${SCRIBE_API_URL}tokens/refresh/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Product': typeof document === 'undefined' ? 'nextjs' : 'browser' },
    body: JSON.stringify({ refresh: refreshToken })
  })
    .then(async (response) => {
      if (response.ok) {
        const { token: newAccessToken, refresh: newRefreshToken } = await response.json();

        setTokenCookie({ access: newAccessToken, refresh: newRefreshToken });
        try {
          const { store } = await import('../store.js');
          const { setToken } = await import('../slices/authSlice');

          // @ts-expect-error - TODO: Add types to Redux
          store.dispatch(setToken({ token: newAccessToken, refresh: newRefreshToken }));

          SentryBreadcrumb.info({ category: 'fetcher', message: 'Successfully refreshed token' });
        } catch (e) {
          SentryBreadcrumb.error({ category: 'fetcher', message: 'Failed to set token in store' });
        }
        return newAccessToken;
      }
      const shouldRedirect = !isCurrentUrlWhiteListed();

      if (response.status === 401 || response.status === 403) {
        SentryBreadcrumb.error({ category: 'fetcher', message: 'Refresh failed, Do we sign-out?', data: { shouldRedirect } });
        if (shouldRedirect) {
          localStorage.setItem('userSignedOut', response.status.toString());
          await signOut(true);
        }
      } else {
        SentryBreadcrumb.error({
          category: 'fetcher',
          message: 'Refresh failed, Unexpected status code',
          data: { 'refreshResponse.status': response.status }
        });
      }
    })
    .finally(() => {
      refreshPromises.delete(userId);
    });

  refreshPromises.set(userId, promise);

  // We need to make sure `.finally` runs before more chaining is added to the promise.
  const output = await promise;

  return output;
};

const fetcher = async <RT = any, B = Record<string, unknown> | FormData | URLSearchParams | undefined>(
  url: string,
  method = 'GET',
  isAuthenticated = false,
  body: B = undefined,
  headersOverride: Record<string, string> = {},
  authToken: string | null = null,
  signal: AbortSignal | undefined = undefined
): Promise<RT> => {
  const headers: Record<string, string> = { 'X-Product': typeof document === 'undefined' ? 'nextjs' : 'browser', ...headersOverride };

  let { token, refresh = undefined } = authToken ? { token: authToken } : getTokenCookie();

  if ((token || refresh) && isAuthenticated) {
    if (!token && refresh) {
      token = await fetchNewAccessToken({ refreshToken: refresh });
    }
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }
  let completeUrl = url.startsWith('http') ? url : `${SCRIBE_API_URL.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;

  const requestAttrs: RequestInit = {
    method,
    credentials: 'include',
    headers,
    signal
  };

  if (method === 'GET') {
    const urlWithParams = new URL(completeUrl);
    const hasEntries = typeof window !== 'undefined' && (body instanceof FormData || body instanceof URLSearchParams);
    const entries = hasEntries ? body.entries() : Object.entries(body || {});

    for (const [key, value] of entries) {
      urlWithParams.searchParams.set(key, `${value}`);
    }
    completeUrl = urlWithParams.href;
  } else if (body instanceof FormData) {
    // Setting "content-type" makes it break. Let `fetch` find out what to do.
    requestAttrs.body = body;
  } else {
    requestAttrs.body = JSON.stringify(body || {});
    headers['Content-Type'] = 'application/json';
  }

  return fetch(completeUrl, requestAttrs)
    .catch((e) => {
      throw e.message === 'Failed to fetch' ? { detail: 'Failed to connect to the server.', status: 0 } : e;
    })
    .then(async (response) => {
      if (response.status !== 401) return response;
      const isWhiteListed = isCurrentUrlWhiteListed();

      SentryBreadcrumb.error({ category: 'fetcher', message: '401 error', data: { isAuthenticated, isWhiteListed, url } });

      if (!isAuthenticated) return response;
      // TODO: Remove the sliding check once it is the only type of token allowed

      if (refresh) {
        SentryBreadcrumb.info({
          category: 'fetcher',
          message: 'Refresh token',
          data: { url }
        });
        try {
          const newAccessToken = await fetchNewAccessToken({ refreshToken: refresh });

          if (newAccessToken) {
            const newHeaders = { ...headers, Authorization: `Bearer ${newAccessToken}` };

            return await fetch(completeUrl, { ...requestAttrs, headers: newHeaders });
          }
        } catch (e) {
          SentryBreadcrumb.error({ category: 'fetcher', message: 'Refreshing failed', data: { url } });
          console.log('Refreshing failed: ', e, { url });
        }
      } else if (!token) {
        SentryBreadcrumb.error({
          category: 'fetcher',
          message: 'No token or refresh found. Do we sign-out?',
          data: { isWhiteListed, url }
        });
        if (!isWhiteListed) {
          await signOut();
        }
      }
      return response;
    })
    .then(async (response) => {
      const text = await response.text();

      if (response.ok) {
        try {
          if (response.status === 204) return {}; // These are enforced to have no content
          return JSON.parse(text);
        } catch {
          return {};
        }
      }

      let payload;

      try {
        payload = JSON.parse(text);
      } catch {
        if (response.status >= 500) {
          // TODO: We probably need more specific error handling here.
          payload = { detail: 'An error occurred in our servers.' };
        } else {
          // TODO: Not sure of the consequences of doing `{ detail: text }` since "text" can be anything
          //  from the server (e.g. stack traces).
          //  Leaving empty to preserve behavior.
          payload = {};
        }
      }

      // TODO: Handle "detail" being an object or a an array of objects/strings.
      throw { ...payload, status: response.status };
    });
};

export default fetcher;
